import {batch} from "react-redux";
import {ALERT_VARIANT, EResponseCode, ERoleId, ETeamRole} from "../../const/dataEnum";
import {
  setAllDepartments,
  setAssignedEmployee,
  setAvailableEmployee,
  setEmployeeIdToAssign,
  setEmployeeIdToRemove,
  setEmployeeIdToRoleChange,
  setHistoryData,
  setHistoryLoading,
  setPeriods,
  setProjects,
  setSaveAssignButton,
  setSelectedPeriod,
  setSelectedProject,
  setShowModalDatePicker,
  setShowModalTeam,
  setTeamAssignmentLoading,
  setTeamProject,
} from "../actions/teamAssignmentAction";
import {setAlert} from "../../stores/actions/alertModalStateAction";
import projectApi from "../../api/rest/projectApi";
import teamProjectApi from "../../api/rest/teamProjectApi";
import departmentApi from "../../api/rest/departmentApi";
import reviewPeriodApi from "../../api/rest/reviewPeriodApi";
import employeeApi from "../../api/rest/employeeApi";

export const populateData = () => async (dispatch, getState) => {
  try {
    const {user} = getState().appState;
    if (!user) return;

    const departments = await departmentApi.getDepartments();

    if (departments) {
      dispatch(setAllDepartments(departments));
    } else {
      dispatch(setAlert({
        show: true,
        variant: ALERT_VARIANT.DANGER,
        message: 'Failed to retrieve department data'
      }));
      return;
    }

    const projects = await fetchProjectsBasedOnRole(user);
    const sortedProjects = [...projects].sort((a, b) =>
      a.projectName.localeCompare(b.projectName)
    );

    dispatch(setProjects(sortedProjects));

    if (sortedProjects.length > 0) {
      dispatch(selectProject(sortedProjects[0]));
    }

  } catch (error) {
    console.error("Error in populateData:", error);
    dispatch(setAlert({
      show: true,
      variant: ALERT_VARIANT.DANGER,
      message: error.message || 'An error occurred while loading data'
    }));
  }
};

const fetchProjectsBasedOnRole = async (user) => {
  if (user.authRole.id === ERoleId.ADMINISTRATOR || user.authRole.id === ERoleId.MANAGER) {
    return await fetchAdminManagerProjects();
  }

  if ([ERoleId.PM, ERoleId.PMD, ERoleId.PRACTICE_LEADER, ERoleId.TEAM_LEADER].includes(user.authRole.id)) {
    return await fetchLeaderProjects(user);
  }

  return [];
};

const fetchAdminManagerProjects = async () => {
  const result = await projectApi.getAll
  return result.responseCode === 0 ? result.data : [];
};

const fetchLeaderProjects = async () => {
  let projects = [];
  const myTeam = await teamProjectApi.getProjectByEmployeeId

  if (myTeam.responseCode === 0) {
    myTeam.data.forEach((team) => {
      if (team.teamRole === ETeamRole.TEAM_LEADER && team.project !== null) {
        projects.push(team.project);
      }
    });
  }

  return projects;
};

export const getPeriod = () => async (dispatch) => {
  const res = reviewPeriodApi.getPeriod

  if (res.data) {
    dispatch(
      setPeriods(
        res.data.data.map((period) => ({
          ...period,
          id: period.id,
          text: period.period,
          group: "",
        }))
      )
    );
  }
};

export const selectProject = (project) => async (dispatch) => {
  try {
    batch(() => {
      dispatch(setHistoryData([]));
      dispatch(setEmployeeIdToRoleChange([]));
      dispatch(setEmployeeIdToAssign([]));
      dispatch(setTeamProject([]));
      dispatch(setSelectedProject(project));
      dispatch(setAssignedEmployee([]));
      dispatch(setSaveAssignButton(false));
    });

    let assignedEmployee = [];
    const teamData = await teamProjectApi.getCurrentTeamMembers(project.id);

    if (teamData) {
      dispatch(setTeamProject(teamData));

      assignedEmployee = teamData.map(team => ({
        id: team.employeeID,
        role: team.teamRole,
        employee: team.employee || {
          practice: {
            department: {
              longName: "Unknown Department"
            }
          }
        },
        department: team.employee?.practice?.department?.longName || "-",
        joinDate: team.joinDate?.slice(0, 10) || "-"
      })).sort((a, b) =>
        a.employee.fullName?.localeCompare(b.employee.fullName) || 0
      );
    }
    dispatch(fetchHistoryData(project.id));
    dispatch(setAssignedEmployee(assignedEmployee));
  } catch (error) {
    console.error("Error in selectProject:", error);
    dispatch(setAlert({
      show: true,
      variant: ALERT_VARIANT.DANGER,
      message: error.message || 'Failed to load project team data'
    }));
    batch(() => {
      dispatch(setTeamProject([]));
      dispatch(setAssignedEmployee([]));
    });
  } finally{
    dispatch(setTeamAssignmentLoading(false))
  }

};

export const assignEvent = (emp) => (dispatch, getState) => {
  const teamAssignmentState = getState().teamAssignmentState;
  const {selectedProject, assignedEmployee, employeeIdToAssign} = teamAssignmentState;
  if (Object.keys(selectedProject).length === 0) return;

  let newAssignedEmployee = [...assignedEmployee];
  let newIdToAssign = [...employeeIdToAssign];

  const isAlreadyAssigned = newAssignedEmployee.some((asg) => asg.id === emp.id);
  const isAlreadyInAssignList = newIdToAssign.includes(emp.id);

  if (!isAlreadyAssigned && !isAlreadyInAssignList) {
    newIdToAssign.push(emp.id);
    newAssignedEmployee.push({
      id: emp.id,
      role: "Team Member",
      employee: emp,
      department: emp.employee?.practice?.department?.longName,
      joinDate: "-"
    });

    batch(() => {
      dispatch(setSaveAssignButton(true));
      dispatch(setAssignedEmployee(newAssignedEmployee));
      dispatch(setEmployeeIdToAssign(newIdToAssign));
    });
  }
};

export const confirmSaveEvent = () => (dispatch) => {
  dispatch(setShowModalDatePicker(true));
};

export const saveAssignEvent = (date) => async (dispatch, getState) => {
  const teamAssignmentState = getState().teamAssignmentState;
  const {
    assignedEmployee,
    employeeIdToAssign,
    employeeIdToRoleChange,
    teamProject,
    selectedProject
  } = teamAssignmentState;
  dispatch(setShowModalDatePicker(false));

  try {
    const employeeToAssign = employeeIdToAssign.map(id => {
      const emp = assignedEmployee.find(e => e.id === id);
      return {
        id: 0,
        joinDate: date,
        teamRole: emp?.role || ETeamRole.TEAM_MEMBER,
        projectID: selectedProject.id,
        employeeID: id,
      };
    });

    const employeeToRoleChange = employeeIdToRoleChange.map(id => {
      const team = teamProject.find(tp => tp.employeeID === id);
      const emp = assignedEmployee.find(e => e.id === id);
      return {
        id: team?.id || 0,
        joinDate: date,
        teamRole: emp?.role || ETeamRole.TEAM_MEMBER,
        projectID: selectedProject.id,
        employeeID: id,
      };
    });

    if (employeeToAssign.length > 0) {
      const response = await teamProjectApi.createTeamAssignments(employeeToAssign);
      if (response.responseCode !== EResponseCode.NO_ERROR) {
        throw new Error(response.message || 'Failed to assign new member(s)');
      }
    }

    if (employeeToRoleChange.length > 0) {
      const responseUpdate = await teamProjectApi.updateTeamRoles(employeeToRoleChange);
      if (responseUpdate.responseCode !== EResponseCode.NO_ERROR) {
        throw new Error(responseUpdate.message || 'Failed to update team roles');
      }
    }

    batch(() => {
      dispatch(selectProject(selectedProject));
      dispatch(setEmployeeIdToAssign([]));
      dispatch(setEmployeeIdToRoleChange([]));
      dispatch(setSaveAssignButton(false));
      dispatch(setAlert({
        show: true,
        variant: ALERT_VARIANT.SUCCESS,
        message: "Team assignment saved successfully!"
      }));
    });

  } catch (error) {
    console.error("Error in saveAssignEvent:", error);
    dispatch(setAlert({
      show: true,
      variant: ALERT_VARIANT.ERROR,
      message: error?.message || 'Failed to save team assignment'
    }));
  }
};


export const assignRole = (id, role) => (dispatch, getState) => {
  const teamAssignmentState = getState().teamAssignmentState;
  const {assignedEmployee, employeeIdToRoleChange, employeeIdToAssign} = teamAssignmentState;

  let newAssignedEmployee = [...assignedEmployee];
  let newEmployeeIdToRoleChange = [...employeeIdToRoleChange];
  let newEmployeeIdToAssign = [...employeeIdToAssign];
  if (!newEmployeeIdToRoleChange.includes(id) && !newEmployeeIdToAssign.includes(id)) {
    newEmployeeIdToRoleChange.push(id);
  }
  const index = newAssignedEmployee.findIndex((asg) => asg.id === id);
  if (index !== -1) {
    newAssignedEmployee[index] = {...newAssignedEmployee[index], role};
  }
  batch(() => {
    dispatch(setEmployeeIdToRoleChange(newEmployeeIdToRoleChange));
    dispatch(setAssignedEmployee(newAssignedEmployee));
    dispatch(setSaveAssignButton(true));
  });
};

export const removeAssignEvent = (id) => async (dispatch) => {
  dispatch(setEmployeeIdToRemove(id));
  dispatch(setShowModalTeam(true));
};

export const confirmRemoveTeam = (selectedPeriodData) => async (dispatch, getState) => {
  try {
    const {
      teamProject,
      selectedProject,
      employeeIdToRemove,
      assignedEmployee,
    } = getState().teamAssignmentState;
    dispatch(setShowModalTeam(false));
    
    const idToRemove = teamProject.find(t => t.employeeID === employeeIdToRemove);
    const response = await teamProjectApi.confirmRemoveTeamMembers(
      idToRemove.id,
      selectedPeriodData.endDate,
      selectedProject.id,
      employeeIdToRemove,
      idToRemove.teamRole,
      idToRemove.joinDate
    );

    const code = response.data?.responseCode;
    const msg = response.data?.message;

    if (code !== 0) {
      dispatch(setAlert({
        show: true,
        variant: ALERT_VARIANT.ERROR,
        message: msg || 'Failed to delete a team member'
      }));
      return;
    }

    const updatedTeamProject = teamProject.filter(t => t.employeeID !== employeeIdToRemove);
    const updatedAssignedEmployee = assignedEmployee.filter(e => e.id !== employeeIdToRemove);

    batch(() => {
      dispatch(setTeamProject(updatedTeamProject));
      dispatch(setAssignedEmployee(updatedAssignedEmployee));
      dispatch(setEmployeeIdToRemove(null));
      dispatch(setAlert({
        show: true,
        variant: ALERT_VARIANT.SUCCESS,
        message: 'Team member successfully deleted'
      }));
    });

  } catch (error) {
    console.error("Error in confirmRemoveTeam:", error);
    dispatch(setAlert({
      show: true,
      variant: ALERT_VARIANT.ERROR,
      message: error.message || 'Network or server error'
    }));
  }
};

export const fetchHistoryData = (projectId) => async (dispatch) => {
  try {
    const responseData = await teamProjectApi.getTeamProjectMembersHistory(projectId);

    const formattedData = Array.isArray(responseData)
      ? responseData.map(item => ({
          ...item,
          employee: item.employee || {
            id: item.employeeID,
            fullName: item.employee?.fullName || "Unknown",
            practice: {
              department: {
                longName: item.departmentName || "-"
              }
            }
          },
          joinDate: item.joinDate?.slice(0, 10) || "-",
          outDate: item.outDate?.slice(0, 10) || "-"
        }))
      : [];

    dispatch(setHistoryData(formattedData));
  } catch (error) {
    console.error("Error in fetchHistoryData:", error);
    dispatch(setHistoryData([])); 
  } finally {
    dispatch(setHistoryLoading(false)); 
  }
};

export const getAvailableEmployee = () => async (dispatch) => {
  try {
    const params ={
      isActve:true,
    }
    const responseData = await employeeApi.getCompleteEmployee(params);
    const data = responseData.data || [];

    const employee = data.map(item => ({
      ...item,
      employee: item.employee || {
        id: item.employeeID,
        fullName: item.employee?.fullName || "Unknown",
        practice: {
          department: {
            longName: item.departmentName || "-"
          }
        }
      },
      joinDate: item.joinDate?.slice(0, 10) || "-"
    }));
    
    dispatch(setAvailableEmployee(employee));
    return employee;
    
  } catch (error) {
    console.error("Error in getAvailableEmployee:", error);
    dispatch(setAlert({
      show: true,
      variant: ALERT_VARIANT.ERROR,
      message: error.message || 'Failed to load available employee data'
    }))
    return [];
  }
};

export const onPeriodChangeHandler = (periodId) => (dispatch) => {
  dispatch(setSelectedPeriod(periodId));
};

export const removeNewAssignEvent = (id) => (dispatch, getState) => {
  const teamAssignmentState = getState().teamAssignmentState;
  const {employeeIdToAssign, assignedEmployee} = teamAssignmentState;
  let newIdToAssign = [...employeeIdToAssign];

  const index = newIdToAssign.indexOf(id);
  if (index > -1) {
    newIdToAssign.splice(index, 1);
  }

  let newAssignedEmployee = assignedEmployee.filter((asg) => asg.id !== id);

  batch(() => {
    dispatch(setEmployeeIdToAssign(newIdToAssign));
    dispatch(setAssignedEmployee(newAssignedEmployee));
    if (newIdToAssign.length < 1) {
      dispatch(setSaveAssignButton(false));
    }
  });
};

export const initializeTeamAssignment = () => async (dispatch) => {
  try {
    await dispatch(populateData());
    await dispatch(getPeriod());
  } catch (error) {
    console.error("Error fetching data:", error);
    dispatch(setAlert({
      show: true,
      variant: ALERT_VARIANT.ERROR,
      message: "Failed to load data"
    }));
  }
};
